import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ToursService} from '../../api/services/tour.service';
import {MapTour} from '../../api/interfaces/map-tour';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {ViewService} from '../../api/services/view.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {Subscription} from 'rxjs';
import {Router, NavigationEnd} from '@angular/router';
import {filter} from 'rxjs/operators';
import {TTSService} from '../../services/tts.service';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-tours-panel',
  template: `
    <div class="story-panel" *ngIf="tour">
      <div class="story-header">
        <button class="reset-btn"
                type="button"
                [matTooltip]="'TOURS.PANEL.RESTART_TOOLTIP' | translate"
                matTooltipPosition="above"
                (click)="replay()">
          <span class="material-symbols-rounded">loop</span>
        </button>
        <span class="story-title">{{ panelTitle }}</span>
        <button class="close-btn"
                type="button"
                [matTooltip]="'TOURS.PANEL.CLOSE_TOOLTIP' | translate"
                matTooltipPosition="above"
                (click)="close()">
          <span class="material-symbols-rounded">close</span>
        </button>
      </div>

      <div class="story-content">
        <ng-container *ngIf="currentStep === 0 && showTourDescription; else stepBody">
          <div class="story-description"
               *ngIf="tour.description"
               [class.animate]="animateStepChange"
               (animationend)="onAnimationEnd()">{{ tour.description }}</div>
        </ng-container>
        <ng-template #stepBody>
          <div class="story-description"
               *ngIf="tour.steps[currentStep - 1]?.savedView?.description"
               [class.animate]="animateStepChange"
               (animationend)="onAnimationEnd()">{{ tour.steps[currentStep - 1].savedView.description }}</div>
        </ng-template>

        <div class="story-controls">
          <button class="ctrl-btn prev-btn"
                  type="button"
                  [matTooltip]="'TOURS.PANEL.PREV_TOOLTIP' | translate"
                  matTooltipPosition="above"
                  (click)="prevStep()"
                  [disabled]="currentStep === 0">
            <span class="material-symbols-rounded">skip_previous</span>
          </button>
          <button class="ctrl-btn play-btn"
                  type="button"
                  *ngIf="!isTourPlaying"
                  [matTooltip]="'TOURS.PANEL.PLAY_TOOLTIP' | translate"
                  matTooltipPosition="above"
                  (click)="togglePlay()"
                  [disabled]="currentStep - 1 === totalSteps - 1">
            <span class="material-symbols-rounded">play_circle</span>
          </button>
          <button class="ctrl-btn play-btn"
                  type="button"
                  *ngIf="isTourPlaying"
                  [matTooltip]="'TOURS.PANEL.PAUSE_TOOLTIP' | translate"
                  matTooltipPosition="above"
                  (click)="togglePlay()">
            <span class="material-symbols-rounded">pause_circle</span>
          </button>
          <button class="ctrl-btn next-btn"
                  type="button"
                  [matTooltip]="'TOURS.PANEL.NEXT_TOOLTIP' | translate"
                  matTooltipPosition="above"
                  (click)="nextStep()"
                  [disabled]="currentStep - 1 === totalSteps - 1">
            <span class="material-symbols-rounded">skip_next</span>
          </button>
        </div>

        <mat-progress-bar *ngIf="isTourPlaying" class="story-progress" [value]="progressSteps"></mat-progress-bar>
      </div>
    </div>
  `,
  styleUrls: ['tours-panel.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressBarModule,
    TranslatePipe
  ],
  standalone: true
})

export class ToursPanelComponent implements OnInit, OnDestroy {
  public tour: MapTour | undefined;
  animateStepChange = false; // Flag to trigger animation
  public isTourPlaying = false;
  currentStep = 0;
  totalSteps = 0;
  showTourDescription = true;
  elapsedTime = 0;
  // How often we update the proggres bar
  intervalDuration = 100;
  progressSteps = 0;
  progressInterval: any;
  playTourTimer: any;
  animationInProgress = false;
  private subscriptions: Subscription[] = [];

  constructor(private tourService: ToursService,
              private savedViewService: ViewService,
              private cdr: ChangeDetectorRef,
              private router: Router,
              private ttsService: TTSService) {
  }

  /**
   * MAP-389: "<story name> • <current scene name>" once a scene is active.
   * Falls back to the story name on the description-only intro step.
   */
  get panelTitle(): string {
    if (!this.tour) return '';
    if (this.currentStep === 0) {
      return this.tour.name;
    }
    const step = this.tour.steps[this.currentStep - 1];
    const sceneName = step?.savedView?.name;
    return sceneName ? `${this.tour.name} • ${sceneName}` : this.tour.name;
  }


  ngOnInit() {
    const tourSub = this.tourService.activateTourSubject
      .subscribe((tour) => {
        if (!tour) {
          this.resetTourState();
          this.tour = undefined;
          return;
        }
        if (this.tour?.id === tour.id) {
          // Same story re-emitted (live edit) — refresh ref without resetting playback.
          this.tour = tour;
          this.totalSteps = tour.steps.length;
          this.cdr.markForCheck();
          return;
        }
        this.resetTourState();
        this.tour = tour;
        this.totalSteps = tour.steps.length;
      });
    this.subscriptions.push(tourSub);

    // Mutual exclusivity: a standalone scene takes over playback.
    const sceneSub = this.savedViewService.activeScene$
      .subscribe((scene) => {
        if (scene && this.tour) {
          this.close();
        }
      });
    this.subscriptions.push(sceneSub);

    const routeSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.tour) {
          this.close();
        }
      });
    this.subscriptions.push(routeSub);
  }

  ngOnDestroy(): void {
    // Clear the interval when the component is destroyed
    this.stopProgress();
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Call this method to start the progress bar animation
  startProgress(duration: number): void {
    this.stopProgress(); // Clear any existing intervals
    this.elapsedTime = 0; // Reset elapsed time

    this.progressInterval = setInterval(() => {
      this.elapsedTime += this.intervalDuration; // Increment the elapsed time
      // Update the progress bar with the new value
      this.progressSteps = (this.elapsedTime / duration) * 100;

      // If the total time has passed, clear the interval
      if (this.elapsedTime >= duration) {
        this.stopProgress();
        this.progressSteps = 100; // Ensure the progress bar shows as complete
      }
    }, this.intervalDuration);
  }

  stopProgress(): void {
    if (this.progressInterval) {
      this.progressSteps = 0;
      this.elapsedTime = 0;
      clearInterval(this.progressInterval);
    }
  }

  replay() {
    if (!this.tour) return;
    this.resetTourState();
  }

  togglePlay() {
    if (!this.tour) return;
    this.isTourPlaying = !this.isTourPlaying;
    if (this.isTourPlaying) {
      this.playNextStepWithDelay();
    }
  }

  playNextStepWithDelay(viewStepAdjustment = 0) {
    if (this.playTourTimer) {
      clearTimeout(this.playTourTimer);
    }
    if (this.currentStep - 1 < this.totalSteps - 1 && this.isTourPlaying) {
      // Get the current step
      const currentStepObj = this.getCurrentStep(viewStepAdjustment);
      if (!currentStepObj) return;
      // Calculate the total duration for this step, including animation speed and wait time
      const totalDuration = currentStepObj.animationSpeed + currentStepObj.pauseDuration;
      // Play the next step
      this.nextStep(true);

      // Wait for the specified duration before playing the next step
      if (this.isTourPlaying) {
        this.playTourTimer = setTimeout(() => {
          this.playNextStepWithDelay(1); // Play the next step with delay
        }, totalDuration);
      }

      // if this is the last step set the flag to false
      if (this.currentStep - 1 === this.totalSteps - 1) {
        this.isTourPlaying = false;
        clearTimeout(this.playTourTimer);
      }
    } else {
      // You've reached the last step, you can perform any final actions here
      this.isTourPlaying = false;
      clearTimeout(this.playTourTimer);
    }
  }


  onAnimationEnd() {
    this.animateStepChange = false;
    this.cdr.detectChanges();
  }

  prevStep() {
    this.animationInProgress = true;
    if (this.currentStep > 1) {
      this.currentStep--;
      this.animateStepChange = true;
      this.cdr.detectChanges();
      if (!this.tour) return;
      this.playProgressIndicator();
      this.savedViewService.playView(
        this.tour.steps[this.currentStep - 1].savedView,
        this.tour.steps[this.currentStep - 1],
        () => {
          this.animationInProgress = false;
        }
      );
    } else if (this.currentStep === 1) {
      this.stopProgress();
      this.currentStep--;
      this.showTourDescription = true;
    }
  }

  nextStep(playViewStep = false) {
    this.animationInProgress = true;
    if (this.currentStep === 0 && !playViewStep) {
      this.showTourDescription = false;
      this.currentStep++;
      if (!this.tour) return;
      this.playProgressIndicator();

      this.savedViewService.playView(
        this.tour.steps[this.currentStep - 1].savedView,
        this.tour.steps[this.currentStep - 1],
        () => {
          this.animationInProgress = false;
        }
      );
    } else {
      this.playProgressIndicator();

      if (this.currentStep - 1 < this.totalSteps - 1) {
        this.currentStep++;
        this.animateStepChange = true;
        this.cdr.detectChanges();
        if (!this.tour) return;
        this.savedViewService.playView(
          this.tour.steps[this.currentStep - 1].savedView,
          this.tour.steps[this.currentStep - 1],
          () => {
            this.animationInProgress = false;
          }
        );
      }
    }
  }

  getCurrentStep(viewStepAdjustment = 1) {
    return this.tour?.steps[this.currentStep - viewStepAdjustment];
  }

  playProgressIndicator() {
    // @ts-ignore
    const currentStepObj = this.tour.steps[this.currentStep];
    if (currentStepObj) {
      this.stopProgress();
      this.startProgress(currentStepObj.animationSpeed + currentStepObj.pauseDuration);
    }
  }

  close() {
    // Stop any ongoing speech when closing tour
    this.ttsService.stop();

    this.resetTourState();
    this.tour = undefined;
    this.tourService.activateTourSubject.next(null);
  }

  private resetTourState() {
    // Stop any ongoing speech when resetting tour state
    this.ttsService.stop();

    this.currentStep = 0;
    this.showTourDescription = true;
    this.isTourPlaying = false;
    this.animationInProgress = false;
    this.animateStepChange = false;
    this.stopProgress();
    if (this.playTourTimer) {
      clearTimeout(this.playTourTimer);
      this.playTourTimer = null;
    }
  }
}
